import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import {
  carbonEmissionFareFilter,
  getHasCarbonEmissionAverageInfo,
} from 'common/emissions';
import { IItinerary, IKeyAnyValue, IKeyStringValue } from 'common/types';
import { getMaxViaCount, getMinutes, hasTravelClubFare } from 'common/utils';
import { TripType } from 'contexts/searchParams';
import { IAirportDetail } from 'hooks/useAirportDetailList';
import { DepartureTime } from 'list/common/filter/DepartureTimeFilter';
import sessionStorage from 'storage/internationalFilter';
import {
  IFareDetail,
  IFareTypeList,
  IInternationalItem,
  IReceivedInternationalResult,
  ISchFare,
  ISchFares,
  ISchedule,
  ISchedules,
  ScheduleKeys,
  SelectTypeKeys,
} from '../types';
import { getDepartureTimeValue, getPriceOfPerson, toDays } from '../utils';
import FilterSet from './filterSet';
import RangeFilter from './rangeFilter';
import { IFilterItem } from './types';

export interface IFilterStore {
  airlineFilter: FilterSet<string>;
  airportFilter: FilterSet<string>[][];
  carbonEmissionOnlyFilter: boolean;
  departureTimeFilters: FilterSet<DepartureTime>[];
  durationFilters: RangeFilter[];
  excludeCardFilter: boolean;
  fareTypeFilter: FilterSet<string>;
  isDirect: boolean;
  isStorageUsed: boolean;
  priceFilter: RangeFilter;
  sameAirlineOnlyFilter: boolean;
  sameAirportOnlyFilter: boolean;
  travelClubOnlyFilter: boolean;
  viaCountFilter: FilterSet<number>;
}

export const initialFilterStore: IFilterStore = {
  airlineFilter: new FilterSet([]),
  airportFilter: [],
  carbonEmissionOnlyFilter: false,
  departureTimeFilters: [],
  durationFilters: [],
  excludeCardFilter: false,
  fareTypeFilter: new FilterSet([]),
  isDirect: false,
  isStorageUsed: false,
  priceFilter: new RangeFilter(0, 0, 0, 0),
  sameAirlineOnlyFilter: false,
  sameAirportOnlyFilter: false,
  travelClubOnlyFilter: false,
  viaCountFilter: new FilterSet([]),
};

export function setFilterStorage(obj: IKeyAnyValue) {
  sessionStorage.set(obj);
}

export function getFilterStorage() {
  return sessionStorage.get();
}

export function clearFilterStorage() {
  sessionStorage.clean();
}

const getMinFareByViaCnt = (fares: ISchFares, schedules: ISchedules[]) =>
  Object.keys(fares).reduce<number[]>((result, scheduleKey) => {
    const scheduleDetails = scheduleKey
      .split('|')
      .map((id, index) => schedules[index]?.[id]);
    if (!scheduleDetails.every(schedule => !!schedule)) return result;
    const viaCnt = getMaxViaCount(scheduleDetails);
    const { fare: fareBySchedule } = fares[scheduleKey];
    const minAdultFare = Object.keys(fareBySchedule).reduce(
      (acc, fareType) =>
        Math.min(
          acc,
          Math.min(
            ...fareBySchedule[fareType].map((el: any) =>
              getPriceOfPerson(el.Adult, true),
            ),
          ),
        ),
      Number.MAX_SAFE_INTEGER,
    );
    // eslint-disable-next-line no-param-reassign
    result[viaCnt] = result[viaCnt]
      ? Math.min(result[viaCnt], minAdultFare)
      : minAdultFare;
    return result;
  }, []);

function getMinMaxFares(fares: ISchFares) {
  return Object.values(fares).reduce(
    ([min, max]: number[], { fare }: ISchFare) => {
      const adultFares = Object.values(fare).flatMap(els =>
        els.map(el => getPriceOfPerson(el.Adult, true)),
      );
      return [Math.min(min, ...adultFares), Math.max(max, ...adultFares)];
    },
    [Number.MAX_SAFE_INTEGER, 0],
  );
}

function createViaCountFilter(
  fares?: ISchFares,
  schedules?: ISchedules[],
  saved?: number[],
  isDirect?: boolean,
) {
  const minFares =
    fares && schedules ? getMinFareByViaCnt(fares, schedules) : null;
  const items: IFilterItem<number>[] = [
    '직항',
    '경유 1회',
    '경유 2회 이상',
  ].map((el, index) => {
    return {
      label: el,
      subLabel: minFares?.[index]?.toString(),
      value: index,
      enable: true,
      selected: isDirect && index === 0 ? true : !!saved?.includes(index),
    };
  });
  return new FilterSet(items);
}

function getMarketingAirlines(schedules: ISchedules[] | undefined) {
  if (!schedules) return new Map();
  const avsBySchedule = schedules.flatMap(schedule => {
    const details = Object.values(schedule).map(({ detail }) => detail);
    const avs = details.map(detail => detail.map(({ av }) => av));
    return avs;
  });
  const flatAvs = avsBySchedule.flat();
  const uniqAvs = uniq(flatAvs);
  return new Map(uniqAvs.map(name => [name, true]));
}

function createStringFilter(
  keyValues?: IKeyStringValue,
  saved?: any,
  _defaultSelected?: boolean,
) {
  const merged = { ...(keyValues ?? {}), ...(saved ?? {}) };

  const hasSaved = Object.keys(saved || {}).length > 0;
  const defaultSelected = (hasSaved ? false : _defaultSelected) ?? false;

  const items: IFilterItem<string>[] = Object.keys(merged).map(el => ({
    label: keyValues?.[el] || saved?.[el],
    value: el,
    enable: true,
    selected:
      (isNil(saved?.[el]) ? undefined : !!saved?.[el]) ?? defaultSelected,
  }));
  return new FilterSet(items);
}

function createDepartureTimeFilter(schedules?: ISchedules[], saved?: string[]) {
  const itinCount = schedules?.length || saved?.length;
  if (!itinCount) {
    return [];
  }
  return [...new Array(itinCount)].map((_, index) => {
    const items: IFilterItem<DepartureTime>[] = [
      DepartureTime.T0,
      DepartureTime.T6,
      DepartureTime.T9,
      DepartureTime.T12,
      DepartureTime.T15,
      DepartureTime.T18,
      DepartureTime.T21,
    ].map(depTime => ({
      label: depTime,
      value: depTime,
      enable: true,
      selected: !!saved?.[index]?.includes(depTime),
    }));
    return new FilterSet(items);
  });
}

function getMinMaxDur(scheduleMap: ISchedules) {
  return Object.values(scheduleMap).reduce(
    (minMax: number[], { journeyTime }: ISchedule) => {
      const totalJrnyTime = getMinutes(journeyTime);
      return minMax.map((v: number, index: number) =>
        index === 0 ? Math.min(v, totalJrnyTime) : Math.max(v, totalJrnyTime),
      );
    },
    [Number.MAX_SAFE_INTEGER, 0],
  );
}

function createDurationFilter(schedules?: ISchedules[], saved?: any) {
  if (!schedules) {
    return [];
  }
  const minMaxEachItin = schedules.map((el: ISchedules) => getMinMaxDur(el));
  return minMaxEachItin.map(([min, max]: number[], index: number) => {
    if (saved[index] > min && saved[index] < max) {
      return new RangeFilter(saved[index], saved[index], min, max);
    }
    return new RangeFilter(min, max, min, max);
  });
}

function createPriceFilter(fares?: ISchFares, saved?: number[]) {
  const [min, max] = fares ? getMinMaxFares(fares) : [0, 0];
  const [savedMin = 0, savedMax = 0] =
    saved && saved.length > 0 ? saved : [min, max];
  return new RangeFilter(savedMin, savedMax, min, max);
}

function createAirportFilter(
  itinerary: IItinerary[],
  trip: string,
  itineraryIataDetails: IAirportDetail[],
  stayLength?: string,
  schedules?: ISchedules[],
) {
  if (!schedules?.length) return [];
  const iataMap = new Map(
    itineraryIataDetails.map(iata => [iata.iataCode, iata]),
  );
  const filters = schedules
    .map((scheduleMap: ISchedules) =>
      Object.values(scheduleMap).reduce(
        (airports, { detail }) => {
          const departureAirport = detail[0].sa;
          const arrivalAirport = detail[detail.length - 1].ea;
          if (!airports.departures.has(departureAirport)) {
            airports.departures.add(departureAirport);
          }
          if (!airports.arrivals.has(arrivalAirport)) {
            airports.arrivals.add(arrivalAirport);
          }
          return airports;
        },
        { departures: new Set<string>(), arrivals: new Set<string>() },
      ),
    )
    .map(({ departures, arrivals }, index) => {
      const { departureAirport, arrivalAirport } = itinerary[index];
      const isDepartureAirport = !!iataMap.get(departureAirport)?.airportName;
      const isArrivalAirport = !!iataMap.get(arrivalAirport)?.airportName;
      return [
        new FilterSet(
          Array.from(departures).map(airport => ({
            label: airport,
            value: airport,
            enable: true,
            selected: !isDepartureAirport || airport === departureAirport,
          })),
        ),
        new FilterSet(
          Array.from(arrivals).map(airport => ({
            label: airport,
            value: airport,
            enable: true,
            selected: !isArrivalAirport || airport === arrivalAirport,
          })),
        ),
      ];
    });
  if (trip !== TripType.RT || stayLength || filters.length !== 2) {
    return filters;
  }
  const [departures, arrivals] = filters[0].map((filter, key) =>
    filter.mergeList(filters[1][+!key]),
  );
  return [
    [departures, arrivals],
    [arrivals, departures],
  ];
}

export const isSameAirportOnlyFilterAvailable = (itinerary: IItinerary[]) =>
  itinerary.length === 2 &&
  (itinerary[0].departureAirport === itinerary[1].arrivalAirport ||
    itinerary[0].arrivalAirport === itinerary[1].departureAirport);

export function loadFilterStore(
  itinerary: IItinerary[],
  trip: string,
  itineraryIataDetails: IAirportDetail[],
  isDirect: boolean = false,
  stayLength?: string,
  input?: IInternationalItem,
): IFilterStore {
  const { airlines, fareTypes, fares, schedules } = input || {};
  const {
    airlines: savedAirlines = {},
    departureTimes: savedDepartureTimes = [],
    durations: savedDurations = [],
    fareTypes: savedFareTypes = {},
    prices: savedPrices = [],
    travelClubOnly: travelClubOnlyFilter = false,
    carbonEmission: carbonEmissionOnlyFilter = false,
    viaCount: savedViaCount = [],
    sameAirlineOnlyFilter = false,
  } = sessionStorage.get() || {};
  const marketingAirlines = getMarketingAirlines(schedules);
  const airlineFilter = createStringFilter(
    airlines,
    savedAirlines,
    true,
  ).filter(({ label, value }) => !!label && !!marketingAirlines.get(value));
  const airportFilter = createAirportFilter(
    itinerary,
    trip,
    itineraryIataDetails,
    stayLength,
    schedules,
  );
  const departureTimeFilters = createDepartureTimeFilter(
    schedules,
    savedDepartureTimes,
  );
  const durationFilters = createDurationFilter(schedules, savedDurations);
  const fareTypeFilter = createStringFilter(fareTypes, savedFareTypes, true);
  const priceFilter = createPriceFilter(fares, savedPrices);
  const viaCountFilter = createViaCountFilter(
    fares,
    schedules,
    savedViaCount,
    isDirect,
  );
  return {
    airlineFilter,
    airportFilter, // do not save
    carbonEmissionOnlyFilter,
    departureTimeFilters,
    durationFilters,
    excludeCardFilter: false, // do not save
    fareTypeFilter,
    isDirect,
    priceFilter,
    sameAirlineOnlyFilter,
    sameAirportOnlyFilter: isSameAirportOnlyFilterAvailable(itinerary), // do not save
    travelClubOnlyFilter,
    viaCountFilter,
    isStorageUsed:
      savedViaCount.length > 0 ||
      Object.values(savedAirlines).length > 0 ||
      Object.values(savedFareTypes).length > 0 ||
      savedDepartureTimes.length > 0 ||
      savedDurations.length > 0 ||
      savedPrices > 0,
  };
}

export function saveFilterStorage(filterStore: IFilterStore) {
  const {
    airlineFilter,
    departureTimeFilters,
    durationFilters,
    fareTypeFilter,
    priceFilter,
    travelClubOnlyFilter,
    viaCountFilter,
    carbonEmissionOnlyFilter,
    sameAirlineOnlyFilter,
  } = filterStore;
  sessionStorage.set({
    airlines: airlineFilter.items
      .filter(el => el.selected)
      .reduce((acc, el) => ({ ...acc, [el.value]: el.label }), {}),
    departureTimes: departureTimeFilters.map(i => i.getSelectedValues()),
    durations: durationFilters.map(i =>
      i.getMax() === i.getTo() ? null : i.getTo(),
    ),
    fareTypes: fareTypeFilter.items
      .filter(el => el.selected)
      .reduce((acc, el) => ({ ...acc, [el.value]: el.label }), {}),
    prices: priceFilter.isActive() ? priceFilter.getFromTo() : null,
    travelClubOnlyFilter,
    carbonEmissionOnlyFilter,
    viaCount: viaCountFilter.getSelectedValues(),
    sameAirlineOnlyFilter,
  });
}

export function resetSelected(
  {
    airlineFilter,
    airportFilter,
    departureTimeFilters,
    durationFilters,
    fareTypeFilter,
    priceFilter,
    viaCountFilter,
  }: IFilterStore,
  itinerary: IItinerary[],
): IFilterStore {
  sessionStorage.clean();
  return {
    airlineFilter: new FilterSet<string>(
      airlineFilter.items.map(item => ({ ...item, selected: true })),
    ),
    airportFilter: airportFilter.map((filters, index) =>
      filters.map(({ items }, isArrival) => {
        const { departureAirport, arrivalAirport } = itinerary[index];
        const airport = isArrival ? arrivalAirport : departureAirport;
        const selectAllAirports = !items.some(({ value }) => value === airport);
        return new FilterSet(
          items.map(item => ({
            ...item,
            selected: selectAllAirports || item.value === airport,
          })),
        );
      }),
    ),
    carbonEmissionOnlyFilter: false,
    departureTimeFilters: departureTimeFilters.map(
      (el, index) =>
        new FilterSet<DepartureTime>(
          departureTimeFilters[index].items.map(item => ({
            ...item,
            selected: false,
          })),
        ),
    ),
    durationFilters: durationFilters.map(
      el => new RangeFilter(el.getMin(), el.getMax(), el.getMin(), el.getMax()),
    ),
    excludeCardFilter: false,
    fareTypeFilter: new FilterSet<string>(
      fareTypeFilter.items.map(item => ({ ...item, selected: true })),
    ),
    isDirect: false,
    isStorageUsed: false,
    priceFilter: new RangeFilter(
      priceFilter.getMin(),
      priceFilter.getMax(),
      priceFilter.getMin(),
      priceFilter.getMax(),
    ),
    sameAirlineOnlyFilter: false,
    sameAirportOnlyFilter: isSameAirportOnlyFilterAvailable(itinerary),
    travelClubOnlyFilter: false,
    viaCountFilter: new FilterSet<number>(
      viaCountFilter.items.map(item => ({ ...item, selected: false })),
    ),
  };
}

export function runFilter(
  filterStore: IFilterStore,
  orgFlights: IReceivedInternationalResult | null,
  itinerary: IItinerary[],
  tripType: string,
  selectType: SelectTypeKeys,
): Map<string, IFareTypeList> {
  const {
    airlineFilter,
    airportFilter,
    carbonEmissionOnlyFilter,
    departureTimeFilters,
    durationFilters,
    excludeCardFilter,
    fareTypeFilter,
    priceFilter,
    sameAirlineOnlyFilter,
    sameAirportOnlyFilter,
    travelClubOnlyFilter,
    viaCountFilter,
  } = filterStore;
  const fares = orgFlights?.results.fares;
  const schedules = orgFlights?.results.schedules;
  if (!schedules || !fares) {
    return new Map();
  }
  const isActiveDepartureTimeFilter = departureTimeFilters.reduce(
    (acc, filterSet) => acc || filterSet.isActive(),
    false,
  );
  const isActiveDurationFilter = durationFilters.reduce(
    (acc, filter) => acc || filter.isActive(),
    false,
  );
  const filterSchedules = (schs: string) => {
    // 각 항공권의 여정별 스캐쥴 배열
    const itinSchedules = schs
      .split('|')
      .map((schId, index) => schedules[index]?.[schId]);
    if (!itinSchedules.every(schedule => !!schedule)) return false;
    const hasSelectedAirport = airportFilter.some(filters =>
      filters.some(filter => filter.isActive()),
    );
    // 직항/경유
    const maxViaCount = getMaxViaCount(itinSchedules);
    // And 조건 필터링
    const firstAirline = itinSchedules[0].detail[0][ScheduleKeys.airlineVendor];
    const andResult = itinSchedules.filter(({ detail, journeyTime }, index) => {
      // 출발시간대
      if (isActiveDepartureTimeFilter) {
        const departureTime: DepartureTime = getDepartureTimeValue(
          toDays(detail[0].sdt),
        );
        if (
          departureTimeFilters[index].isActive() &&
          !departureTimeFilters[index].hasSelected(departureTime)
        ) {
          return false;
        }
      }
      // 소요시간
      if (
        isActiveDurationFilter &&
        durationFilters[index].isActive() &&
        !durationFilters[index].include(getMinutes(journeyTime))
      ) {
        return false;
      }
      // 공항
      const [departures, arrivals] = airportFilter[index];
      const departureAirport = detail[0].sa;
      const arrivalAirport = detail[detail.length - 1].ea;
      if (
        hasSelectedAirport &&
        (!departures.hasSelected(departureAirport) ||
          !arrivals.hasSelected(arrivalAirport))
      ) {
        return false;
      }
      const prev = itinSchedules[index - 1]?.detail;
      const prevDepartureAirport = prev?.[0].sa;
      const prevArrivalAirport = prev?.[prev.length - 1].ea;
      if (
        sameAirportOnlyFilter &&
        index > 0 &&
        !(
          tripType === TripType.RT &&
          departureAirport === prevArrivalAirport &&
          arrivalAirport === prevDepartureAirport
        ) &&
        !(
          tripType === TripType.MD &&
          ((itinerary[index].departureAirport ===
            itinerary[index - 1].arrivalAirport &&
            departureAirport === prevArrivalAirport) ||
            (itinerary[index].arrivalAirport ===
              itinerary[index - 1].departureAirport &&
              arrivalAirport === prevDepartureAirport))
        )
      ) {
        return false;
      }
      if (
        sameAirlineOnlyFilter &&
        detail.some(({ av }) => av !== firstAirline)
      ) {
        return false;
      }
      return true;
    });
    // Or 조건 필터링
    const orResult = itinSchedules.filter(({ detail }) => {
      // 항공사
      if (airlineFilter.isActive()) {
        const filtered = detail.filter(el => airlineFilter.hasSelected(el.av));
        if (filtered.length === 0) {
          return false;
        }
      }
      return true;
    });
    return (
      (!viaCountFilter.isActive() || viaCountFilter.hasSelected(maxViaCount)) &&
      andResult.length === itinSchedules.length &&
      orResult.length > 0
    );
  };

  const hasCarbonEmissionAverageInfo =
    getHasCarbonEmissionAverageInfo(orgFlights);

  return new Map(
    Object.entries(fares)
      ?.filter(([schs]) => {
        if (carbonEmissionOnlyFilter) {
          return carbonEmissionFareFilter(
            hasCarbonEmissionAverageInfo,
            orgFlights,
            selectType,
          )(schs);
        }
        return true;
      })
      ?.filter(([schs]) => filterSchedules(schs))
      .map(([k, { fare }]): [string, IFareTypeList] => {
        const filtered: IFareTypeList = Object.entries(fare).reduce(
          (acc: IFareTypeList, [fareType, fareDetails]) => {
            if (
              (excludeCardFilter && fareType.includes('/B')) ||
              (fareTypeFilter.isActive() &&
                !fareTypeFilter.hasSelected(fareType))
            ) {
              return acc;
            }
            const filteredPrices = fareDetails
              .filter((el: IFareDetail) =>
                priceFilter.isActive()
                  ? priceFilter.include(getPriceOfPerson(el.Adult, true))
                  : true,
              )
              .sort(
                (a, b) =>
                  getPriceOfPerson(a.Adult, true) -
                  getPriceOfPerson(b.Adult, true),
              );
            if (travelClubOnlyFilter && !hasTravelClubFare(fareDetails)) {
              return acc;
            }
            if (filteredPrices.length === 0) {
              return acc;
            }
            return { ...acc, [fareType]: filteredPrices };
          },
          {},
        );
        return [k, filtered];
      })
      .filter(([, values]) => Object.keys(values).length > 0),
  );
}
